import {workingHourForTheDay, getHolidaysOfTheDay} from '@/forecast-app/my-work-tab/my-timesheets-page/timesheets_person_data';
import {getLockedDate} from '@/components/timesheets/time-lock/TimeLockUtil';
import {APPROVAL_STATUS} from '@/constants';
import Moment from 'moment';

/**
 * Last day of the visible week where the person is actively working
 * (working-hours > 0 AND no holiday on the holiday calendar). Iterates the
 * week from end backward, returning the first matching day.
 *
 * Returns `weekViewDate` (a fallback Moment) if no working day is found in
 * the week (e.g. all-holiday week, or person inactive for the entire week).
 *
 * Centralised here so both My Timesheets and the Time Approval page synthesis
 * use the same primitive, preventing drift between the two consumers.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lastWorkingDayInWeek = (person: any, weekViewDate: any): any => {
	const weekStart = weekViewDate.clone().startOf('week');
	const weekArray = Array.from({length: 7}, (_, i) => weekStart.clone().add(i, 'day'));
	const found = weekArray
		.slice()
		.reverse()
		.find(day => {
			const workingHours = workingHourForTheDay(person, day);
			const holidaysForDay = getHolidaysOfTheDay(person, day);
			return workingHours !== 0 && holidaysForDay.length === 0;
		});
	return found ?? weekViewDate;
};

/**
 * Whether a row's `approvalStatus` is a real signal of where its week has got to.
 *
 * A row's status only means something where the company approves that row's category. Where it
 * does not, `ApprovalStatus.inferApprovalStatus` births the row APPROVED
 * (`ApprovalStatus.java:22-25`, the `else` branch) and its status is a default, not a statement
 * about the week's progress. That applies to **project** rows as much as to idle ones: in a
 * company with Timesheet approval off, an ordinary project-only week is made entirely of
 * born-APPROVED rows, and counting them would report the week APPROVED. Counting a company's
 * auto-approved PTO does the same thing, shadowing the submission-lock fallback below and
 * taking Unsubmit away from an owner who has something to correct.
 *
 * Internal time takes the Timesheet toggle too, and here the predicate deliberately does *not*
 * mirror `inferApprovalStatus`, which births an internal row PENDING off
 * `usingInternalTimeApproval` alone. The write path is the stricter of the two — both
 * `getApprovableScopeCondition` (`TimeRegLogic.java:4523`) and the mutation
 * (`TimeRegLogic.java:1154`) AND in `usingTimeApproval`, because Internal time is nested under
 * Timesheet approval in the admin UI and a stale `useInternalTimeApproval` left behind by
 * turning Timesheet approval off must not readmit it. Reading with the looser rule would let
 * such a row vote the week PENDING while no verb server-side can move it: permanently pending,
 * with nothing to press. Read and write have to agree on which rows are in the workflow, so the
 * read side follows the write side.
 *
 * The TIME_LOCKING module check that `FeatureUtil` pairs these company flags with is
 * deliberately not repeated here — both callers gate on it before reaching this helper, and
 * repeating it would make an otherwise pure function depend on module globals.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const carriesApprovalStatus = (row: any, company: any): boolean => {
	if (!row?.idleTime) return !!company?.useTimeApproval;
	return row.idleTime.isInternalTime
		? !!company?.useTimeApproval && !!company?.useInternalTimeApproval
		: !!company?.useTimeOffApproval;
};

/**
 * Single source of truth for a person's week-level approval status.
 *
 * Precedence (highest first):
 *   1. If `timeRegs` contains any row whose status counts (see `carriesApprovalStatus`),
 *      derive status from row.approvalStatus values. Under the new statuses that row
 *      hierarchy is action-first — DECLINED > PENDING > SUBMITTED > APPROVED > INVOICED —
 *      so a week reads as the next thing its owner has to do to it.
 *   2. If `personWeekApprovals` contains a row for this week with status APPROVED or
 *      DECLINED, return that (sticky approver decision). Status SUBMITTED in that row is
 *      neutral — falls through to step 3.
 *   3. If no row's status counts AND the person's effective submission lock is at or after
 *      `lastWorkingDayInWeek(person, weekViewDate)`, return SUBMITTED. When the caller is looking
 *      at a single day (`viewedDay`), the lock only has to reach that day — or the week's last
 *      working day, if that comes first, so a non-working day after it reads like the week.
 *      Otherwise a blank day inside a submitted range reads as outstanding, and the period
 *      offers Submit where its owner needs Unsubmit to reopen it.
 *   4. Otherwise return NOT_SUBMITTED (FF OFF) or PENDING (FF ON).
 *
 * Why this precedence: time-reg rows are the more specific signal. Week-approval rows
 * APPROVED/DECLINED are the approver's explicit decision (sticky). The lock-check
 * is the lowest-precision signal — only fires when nothing more specific applies.
 *
 * Which rows get a vote differs by flag state. Under the new statuses the cut is by category
 * approval rather than by kind of row (see `carriesApprovalStatus`): internal time and time off
 * follow the same lifecycle as project time wherever the company approves their category, so a
 * decline of one is a decline of the week and has to reach the badge and the Resubmit button —
 * and project time stops voting where the company does not approve timesheets, since there its
 * rows are born approved and say nothing. A week can therefore have no voting rows at all
 * despite holding hours, which falls through to step 3 by design.
 *
 * Without the flag the scope stays project-only regardless of the toggles: the weakest-link rule
 * below would otherwise drag a week with project time submitted and a time-off row unsubmitted
 * back to NOT_SUBMITTED, which is a status change for weeks that behaved fine before.
 *
 * Consumers:
 *   - TimesheetsBottomSection.getTimesheetsApprovalStatus (My Timesheets)
 *   - TimeApprovalSection synthesis filter (Time Approval page)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getApprovalStatusForWeek = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	person: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	company: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	weekViewDate: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	timeRegs: any[] | null | undefined,
	hasNewStatuses: boolean,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	personWeekApprovals: any[] | null | undefined = [],
	// The single day in view (Day view), or null when the period is the whole week.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	viewedDay: any = null
): string => {
	const rows = timeRegs ?? [];
	// A null entry is a real input here: `getTimesheetsApprovalStatus` adapts Relay edges with
	// `e?.node ?? e`, so a null edge arrives as a null row. The legacy branch below already
	// tolerates one (`if (!row || ...)`), so it keeps them; the hierarchy below reads
	// `row.approvalStatus` unguarded, so drop them there rather than throwing mid-render.
	const statusRows = hasNewStatuses
		? rows.filter(t => t && carriesApprovalStatus(t, company))
		: rows.filter(t => !t?.idleTime);

	// Step 2: week-approval row sticky-state check. Only APPROVED/DECLINED count;
	// SUBMITTED is neutral (defer to lock-based step 3).
	if (statusRows.length === 0) {
		const weekStart = weekViewDate.clone().startOf('week');
		const pwaRow = (personWeekApprovals ?? []).find(
			(r: {weekStartYear: number; weekStartMonth: number; weekStartDay: number}) =>
				r.weekStartYear === weekStart.year() &&
				r.weekStartMonth === weekStart.month() + 1 &&
				r.weekStartDay === weekStart.date()
		);
		if (
			pwaRow &&
			(pwaRow.approvalStatus === APPROVAL_STATUS.APPROVED || pwaRow.approvalStatus === APPROVAL_STATUS.DECLINED)
		) {
			return pwaRow.approvalStatus;
		}
	}

	// Step 3 + 4: no row's status counts → consult lock
	if (statusRows.length === 0) {
		const lockedDate = getLockedDate(company, person);
		const lastWorkingDay = lastWorkingDayInWeek(person, weekViewDate);
		const lockMustReach = viewedDay && lastWorkingDay ? Moment.min(viewedDay, lastWorkingDay) : lastWorkingDay;
		if (lockedDate && lockMustReach && lockedDate.isSameOrAfter(lockMustReach, 'day')) {
			return APPROVAL_STATUS.SUBMITTED;
		}
		return hasNewStatuses ? APPROVAL_STATUS.PENDING : APPROVAL_STATUS.NOT_SUBMITTED;
	}

	// Step 1 (FF OFF): mirror existing TimesheetsBottomSection logic — weakest-link semantics.
	// Any NOT_SUBMITTED row drags the week to NOT_SUBMITTED, even if other rows are SUBMITTED.
	if (!hasNewStatuses) {
		let status: string = APPROVAL_STATUS.APPROVED;
		for (const row of statusRows) {
			if (!row || row.approvalStatus === APPROVAL_STATUS.NOT_SUBMITTED) {
				return APPROVAL_STATUS.NOT_SUBMITTED;
			} else if (row.approvalStatus === APPROVAL_STATUS.SUBMITTED) {
				status = APPROVAL_STATUS.SUBMITTED;
			}
		}
		return status;
	}

	// FF on: full new status hierarchy, ordered by what the week still asks of its owner rather
	// than by how far it has progressed. A week reads as whatever needs doing to it next.
	//
	// PENDING therefore outranks SUBMITTED: a week reported SUBMITTED the moment a single row
	// was, so a part-submitted week claimed to be finished while the rest of it had never
	// reached an approver — and the single action button, which reads this status, offered only
	// Unsubmit. Ranking the outstanding time first makes the badge and the button agree on the
	// thing the owner still has to do.
	//
	// DECLINED stays above it: the approver has asked for a correction, and that is the more
	// urgent ask. APPROVED and INVOICED stay at the bottom — they are terminal, and any row
	// short of them is what the week is still waiting on.
	if (statusRows.some(t => t.approvalStatus === APPROVAL_STATUS.DECLINED)) return APPROVAL_STATUS.DECLINED;
	if (statusRows.some(t => t.approvalStatus === APPROVAL_STATUS.PENDING)) return APPROVAL_STATUS.PENDING;
	if (statusRows.some(t => t.approvalStatus === APPROVAL_STATUS.NOT_SUBMITTED)) return APPROVAL_STATUS.PENDING;
	if (statusRows.some(t => t.approvalStatus === APPROVAL_STATUS.SUBMITTED)) return APPROVAL_STATUS.SUBMITTED;
	if (statusRows.some(t => t.approvalStatus === APPROVAL_STATUS.APPROVED)) return APPROVAL_STATUS.APPROVED;
	if (statusRows.some(t => t.approvalStatus === APPROVAL_STATUS.INVOICED)) return APPROVAL_STATUS.INVOICED;
	return APPROVAL_STATUS.NOT_SUBMITTED;
};

/**
 * Group owned-projects-persons by their week-level computed status.
 *
 * Used by the Time Approval page synthesis to route each owned person to the
 * matching bucket: NOT_SUBMITTED → Not Submitted tab, PENDING → Pending tab,
 * SUBMITTED → Submitted tab (the 0h-locked case), etc. Persons with time-regs
 * land in whatever bucket their row statuses imply (via getApprovalStatusForWeek);
 * persons without time-regs land in SUBMITTED if their effective lock covers the
 * week, otherwise in NOT_SUBMITTED (FF OFF) / PENDING (FF ON).
 *
 * Returns a Map keyed by APPROVAL_STATUS value. Empty inputs return an empty Map
 * (callers use `Map.get(status) ?? []` to handle missing buckets).
 *
 * Time-regs without a person.id (orphan rows) are silently skipped — they can't
 * be attributed to a person, so they don't influence anyone's status.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const synthesizeOwnedPersonsByStatus = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ownedPersonsEdges: any[] | null | undefined,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	timeRegs: any[] | null | undefined,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	company: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	weekViewDate: any,
	hasNewStatuses: boolean
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Map<string, any[]> => {
	const timeRegsByPersonId = new Map();
	(timeRegs ?? []).forEach(tReg => {
		const pid = tReg.person?.id;
		if (!pid) return;
		const arr = timeRegsByPersonId.get(pid) ?? [];
		arr.push(tReg);
		timeRegsByPersonId.set(pid, arr);
	});

	const byStatus = new Map();
	(ownedPersonsEdges ?? []).forEach(p => {
		const personRows = timeRegsByPersonId.get(p.node.id) ?? [];
		// PIKA-519: pass the person's weekApprovals through so step 2 of the precedence
		// can pick up APPROVED/DECLINED 0h-week states. Persons fetched without this
		// field (older callers) get [] and behave per pre-PIKA-519 precedence.
		const pwaRows = p.node.weekApprovals ?? [];
		const status = getApprovalStatusForWeek(p.node, company, weekViewDate, personRows, hasNewStatuses, pwaRows);
		if (!byStatus.has(status)) byStatus.set(status, []);
		byStatus.get(status).push(p);
	});
	return byStatus;
};
